import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  ClipboardList, Plus, Pencil, Trash2, FileDown, Mail, Star,
  BookOpenCheck, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 0 (red) -> 6 (green), matching the Scoring Rubric colour ramp
const LEVEL_COLORS = ['#d64545', '#e07b39', '#e8a13a', '#e6c34a', '#b9c94a', '#8fbf4f', '#5aab3d'];
const CX_STYLE = {
  1: { solid: 'bg-emerald-500 text-white border-emerald-500', dot: 'bg-emerald-500', text: 'text-emerald-600' },
  2: { solid: 'bg-amber-500 text-white border-amber-500', dot: 'bg-amber-500', text: 'text-amber-600' },
  3: { solid: 'bg-rose-500 text-white border-rose-500', dot: 'bg-rose-500', text: 'text-rose-600' },
};

const emptyTask = () => ({ name: '', complexity: null, locked: false, initiate: null, quality: null, pace: null, completion: null });
const todayStr = () => new Date().toISOString().slice(0, 10);

const ScorePicker = ({ value, onChange, levels, label, testid }) => (
  <div className="flex items-center gap-0.5" role="group" aria-label={`Score for ${label}`}>
    <button type="button" onClick={() => onChange(null)} title="Not assessed"
      className={`px-1.5 h-7 rounded text-[11px] font-semibold border transition-colors ${
        value === null || value === undefined ? 'bg-slate-500 border-slate-500 text-white' : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400'
      }`}
      data-testid={`${testid}-na`}>
      N/A
    </button>
    {[0, 1, 2, 3, 4, 5, 6].map((n) => {
      const selected = value === n;
      return (
        <button key={n} type="button" onClick={() => onChange(n)}
          title={levels[n] || String(n)}
          style={selected ? { backgroundColor: LEVEL_COLORS[n], borderColor: LEVEL_COLORS[n] } : undefined}
          className={`w-7 h-7 rounded text-[12px] font-bold border transition-colors ${
            selected ? 'text-white' : 'bg-white border-slate-300 text-slate-600'
          }`}
          data-testid={`${testid}-${n}`}>
          {n}
        </button>
      );
    })}
  </div>
);

const avgOf = (vals) => {
  const nums = vals.filter((v) => v !== null && v !== undefined);
  if (!nums.length) return null;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
};

export const JobsiteReports = ({ students, batches }) => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [dirSites, setDirSites] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterBatch, setFilterBatch] = useState('all');
  const [filterIntern, setFilterIntern] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const [emailList, setEmailList] = useState('');
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(null);
  const [view, setView] = useState('list');
  const [trendIntern, setTrendIntern] = useState('');
  const [trendMetric, setTrendMetric] = useState('overall');
  const [trendReports, setTrendReports] = useState([]);

  const levels = {};
  (meta?.levels || []).forEach((l) => { levels[l.value] = l.label; });

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterBatch !== 'all') params.batch_id = filterBatch;
      if (filterIntern !== 'all') params.intern_id = filterIntern;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/jobsite-reports`, { params });
      setReports(res.data);
    } catch { /* handled by empty state */ }
    setLoading(false);
  }, [filterBatch, filterIntern, dateFrom, dateTo]);

  useEffect(() => {
    axios.get(`${API}/jobsite-reports/meta`).then((r) => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/job-sites`).then((r) => setDirSites(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchReports(); }, [fetchReports]);

  useEffect(() => {
    if (!trendIntern) { setTrendReports([]); return; }
    axios.get(`${API}/jobsite-reports`, { params: { intern_id: trendIntern } })
      .then((r) => setTrendReports(r.data))
      .catch(() => {});
  }, [trendIntern]);

  const trendData = [...trendReports]
    .sort((a, b) => (a.report_date > b.report_date ? 1 : -1))
    .map((r) => {
      const point = { date: r.report_date, jobsite: r.jobsite };
      point.tasks = avgOf((r.tasks || []).flatMap((t) => [t.initiate, t.quality, t.pace, t.completion]));
      point.skills = avgOf(Object.values(r.soft_skills || {}));
      (meta?.task_dims || []).forEach((d) => {
        point[d.key] = avgOf((r.tasks || []).map((t) => t[d.key]));
      });
      (meta?.soft_skills || []).forEach((s) => {
        point[`ss_${s.key}`] = r.soft_skills?.[s.key] ?? null;
      });
      return point;
    });

  const blankForm = () => ({
    intern_id: '', report_date: todayStr(), jobsite: '', job_site_id: '', attendance: '',
    tasks: [],
    soft_skills: Object.fromEntries((meta?.soft_skills || []).map((s) => [s.key, null])),
    notes: '',
  });

  const openNew = () => { setEditId(null); setForm(blankForm()); setFormOpen(true); };
  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      intern_id: r.intern_id, report_date: r.report_date, jobsite: r.jobsite || '', job_site_id: r.job_site_id || '', attendance: r.attendance || '',
      tasks: (r.tasks || []).map((t) => ({ ...emptyTask(), ...t })),
      soft_skills: { ...Object.fromEntries((meta?.soft_skills || []).map((s) => [s.key, null])), ...(r.soft_skills || {}) },
      notes: r.notes || '',
    });
    setFormOpen(true);
  };

  const selectJobSite = (id) => {
    const site = dirSites.find((s) => s.id === id);
    const locked = (site?.tasks || []).map((t) => ({
      name: t.description, complexity: t.complexity, locked: true,
      initiate: null, quality: null, pace: null, completion: null,
    }));
    setForm((p) => ({
      ...p,
      job_site_id: id,
      jobsite: site?.name || '',
      tasks: [...locked, ...p.tasks.filter((t) => !t.locked && (t.name || '').trim())],
    }));
  };

  const save = async () => {
    if (!form.intern_id) { toast.error('Pick an intern'); return; }
    if (!form.report_date) { toast.error('Pick a date'); return; }
    if (!form.job_site_id && !editId) { toast.error('Pick a job site'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editId) await axios.put(`${API}/jobsite-reports/${editId}`, payload);
      else await axios.post(`${API}/jobsite-reports`, payload);
      toast.success(editId ? 'Report updated' : 'Report saved');
      setFormOpen(false);
      fetchReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the report');
    }
    setSaving(false);
  };

  const remove = async (r) => {
    try {
      await axios.delete(`${API}/jobsite-reports/${r.id}`);
      toast.success('Report deleted');
      fetchReports();
    } catch { toast.error('Could not delete'); }
  };

  const downloadPdf = async (r) => {
    try {
      const res = await axios.get(`${API}/jobsite-reports/${r.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Jobsite-Report-${r.intern_name}-${r.report_date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not generate the PDF'); }
  };

  const sendEmail = async () => {
    try {
      const res = await axios.post(`${API}/jobsite-reports/${emailFor.id}/email`, { emails: emailList });
      toast.success(`Report emailed to ${res.data.queued} recipient${res.data.queued === 1 ? '' : 's'}`);
      setEmailFor(null);
      setEmailList('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the report');
    }
  };

  const draftStory = (r) => {
    const highlights = (r.tasks || []).filter((t) => t.name).map((t) => t.name).join(', ');
    const story = `${r.intern_name} had a great day at ${r.jobsite || 'their job site'} on ${r.report_date}.` +
      (highlights ? ` They worked on: ${highlights}.` : '') +
      (r.notes ? `\n\n${r.notes}` : '');
    sessionStorage.setItem('ss_prefill', JSON.stringify({
      student_name: r.intern_name,
      headline: `${r.intern_name} shines at ${r.jobsite || 'the job site'}`,
      story,
    }));
    navigate('/dashboard');
    toast.info('Drafting a success story from this report…');
  };

  const internsForFilter = filterBatch === 'all' ? students : students.filter((s) => s.batch_id === filterBatch);
  const batchName = (id) => batches.find((b) => b.id === id)?.name || '';

  if (!meta) {
    return <div className="p-8 text-center text-[#5C5C5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading jobsite reports…</div>;
  }

  return (
    <div className="space-y-4" data-testid="jobsite-reports">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#34657f]" /> Daily Jobsite Reports
          </h2>
          <p className="text-sm text-[#5C5C5C]">Score job tasks and soft skills on the 0–6 rubric — one report per intern per shift.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#b34e00] text-[#b34e00] hover:bg-[#b34e00] hover:text-white"
            onClick={() => navigate('/rubric')} data-testid="jr-rubric-btn">
            <BookOpenCheck className="w-4 h-4 mr-1.5" /> Reference Full Rubric
          </Button>
          <Button className="bg-[#3d7a22] hover:bg-[#5aab3d]" onClick={openNew} data-testid="jr-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New report
          </Button>
        </div>
      </div>

      <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1 gap-1" role="tablist" aria-label="Jobsite report views">
        {[{ id: 'list', label: 'Reports' }, { id: 'trends', label: 'Progress Trends' }].map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={view === t.id}
            onClick={() => setView(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              view === t.id ? 'bg-[#34657f] text-white' : 'text-[#5C5C5C] hover:text-[#1A1A1A]'
            }`}
            data-testid={`jr-view-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {view === 'trends' && (
        <div className="space-y-4" data-testid="jr-trends">
          <Card className="dashboard-card">
            <CardContent className="p-4 flex flex-wrap gap-3 items-end">
              <div className="w-56">
                <Label className="text-xs">Intern</Label>
                <Select value={trendIntern} onValueChange={setTrendIntern}>
                  <SelectTrigger data-testid="jr-trend-intern"><SelectValue placeholder="Choose an intern…" /></SelectTrigger>
                  <SelectContent>
                    {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-64">
                <Label className="text-xs">Metric</Label>
                <Select value={trendMetric} onValueChange={setTrendMetric}>
                  <SelectTrigger data-testid="jr-trend-metric"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="overall">Overall (tasks vs soft skills)</SelectItem>
                    {(meta.task_dims || []).map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                    {(meta.soft_skills || []).map((s) => <SelectItem key={s.key} value={`ss_${s.key}`}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {trendIntern && <p className="text-sm text-[#5C5C5C] pb-2">{trendData.length} report{trendData.length === 1 ? '' : 's'}</p>}
            </CardContent>
          </Card>
          {!trendIntern ? (
            <Card className="dashboard-card"><CardContent className="p-10 text-center text-[#5C5C5C]">
              Pick an intern to chart their rubric scores over time — perfect for IEP meetings.
            </CardContent></Card>
          ) : trendData.length < 2 ? (
            <Card className="dashboard-card"><CardContent className="p-10 text-center text-[#5C5C5C]" data-testid="jr-trend-empty">
              Not enough reports yet ({trendData.length}). File at least two daily reports to see a trend line.
            </CardContent></Card>
          ) : (
            <Card className="dashboard-card">
              <CardContent className="p-5">
                <div className="h-80" data-testid="jr-trend-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 10, right: 20, bottom: 5, left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#dde5ea" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 6]} ticks={[0, 1, 2, 3, 4, 5, 6]} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      {trendMetric === 'overall' ? (
                        <>
                          <Line type="monotone" dataKey="tasks" name="Job tasks avg" stroke="#34657f" strokeWidth={2.5} connectNulls dot={{ r: 4 }} />
                          <Line type="monotone" dataKey="skills" name="Soft skills avg" stroke="#e87722" strokeWidth={2.5} connectNulls dot={{ r: 4 }} />
                        </>
                      ) : (
                        <Line type="monotone" dataKey={trendMetric} connectNulls
                          name={[...(meta.task_dims || []), ...(meta.soft_skills || []).map((s) => ({ ...s, key: `ss_${s.key}` }))].find((d) => d.key === trendMetric)?.label || trendMetric}
                          stroke="#34657f" strokeWidth={2.5} dot={{ r: 4 }} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-[#5C5C5C] mt-2">6 = independent to employer standard · 0 = refused. Gaps mean the item wasn't scored that day.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {view === 'list' && (<>
      <Card className="dashboard-card">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="w-44">
            <Label className="text-xs">Class</Label>
            <Select value={filterBatch} onValueChange={(v) => { setFilterBatch(v); setFilterIntern('all'); }}>
              <SelectTrigger data-testid="jr-filter-batch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Label className="text-xs">Intern</Label>
            <Select value={filterIntern} onValueChange={setFilterIntern}>
              <SelectTrigger data-testid="jr-filter-intern"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All interns</SelectItem>
                {internsForFilter.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="jr-filter-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="jr-filter-to" />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="p-8 text-center text-[#5C5C5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
      ) : reports.length === 0 ? (
        <Card className="dashboard-card"><CardContent className="p-10 text-center text-[#5C5C5C]" data-testid="jr-empty">
          No reports yet for this filter. Click "New report" to file today's first one.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {reports.map((r) => {
            const taskAvg = avgOf((r.tasks || []).flatMap((t) => [t.initiate, t.quality, t.pace, t.completion]));
            const skillAvg = avgOf(Object.values(r.soft_skills || {}));
            return (
              <Card key={r.id} className="dashboard-card" data-testid={`jr-report-${r.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[#1A1A1A]">
                      {r.intern_name}
                      <span className="text-xs font-normal text-[#5C5C5C] ml-2">{batchName(r.batch_id)}</span>
                    </p>
                    <p className="text-sm text-[#5C5C5C]">
                      {r.report_date}{r.jobsite ? ` · ${r.jobsite}` : ''} · by {r.created_by_name}
                    </p>
                    {r.attendance && (
                      <span className="inline-block mt-1 text-[10px] font-medium bg-[#34657f]/10 text-[#34657f] px-1.5 py-0.5 rounded-full" data-testid={`jr-attendance-${r.id}`}>{r.attendance}</span>
                    )}
                  </div>
                  <div className="flex gap-4 text-center shrink-0">
                    <div>
                      <p className="text-lg font-bold text-[#34657f]">{taskAvg ?? '—'}</p>
                      <p className="text-[10px] uppercase text-[#5C5C5C]">Tasks avg</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-[#b34e00]">{skillAvg ?? '—'}</p>
                      <p className="text-[10px] uppercase text-[#5C5C5C]">Skills avg</p>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} aria-label="Edit report" data-testid={`jr-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)} aria-label="Download PDF" data-testid={`jr-pdf-${r.id}`}><FileDown className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEmailFor(r)} aria-label="Email report" data-testid={`jr-email-${r.id}`}><Mail className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => draftStory(r)} aria-label="Draft success story" title="Draft a success story from this report" data-testid={`jr-story-${r.id}`}><Star className="w-4 h-4 text-[#e87722]" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(r)} aria-label="Delete report" data-testid={`jr-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      </>)}

      {/* new / edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit jobsite report' : 'New daily jobsite report'}</DialogTitle>
            <DialogDescription>
              Score each item 0–6 (hover a number for the rubric meaning) or leave N/A.
            </DialogDescription>
            <Button type="button" className="w-fit bg-[#b34e00] hover:bg-[#e87722] text-white"
              onClick={() => window.open('/rubric', '_blank')} data-testid="jr-form-rubric-btn">
              <BookOpenCheck className="w-4 h-4 mr-1.5" /> Reference Full Rubric
            </Button>
          </DialogHeader>
          {form && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Intern *</Label>
                  <Select value={form.intern_id} onValueChange={(v) => setForm((p) => ({ ...p, intern_id: v }))}>
                    <SelectTrigger data-testid="jr-form-intern"><SelectValue placeholder="Choose intern…" /></SelectTrigger>
                    <SelectContent>
                      {students.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}{batchName(s.batch_id) ? ` — ${batchName(s.batch_id)}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={form.report_date} onChange={(e) => setForm((p) => ({ ...p, report_date: e.target.value }))} data-testid="jr-form-date" />
                </div>
                <div>
                  <Label>Job site *</Label>
                  <Select value={form.job_site_id} onValueChange={selectJobSite}>
                    <SelectTrigger data-testid="jr-form-jobsite"><SelectValue placeholder="Choose a job site…" /></SelectTrigger>
                    <SelectContent>
                      {dirSites.map((j) => <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {dirSites.length === 0 && (
                    <p className="text-[11px] text-amber-600 mt-1">No job sites yet — add them in the Program Directory.</p>
                  )}
                  {form.job_site_id && (
                    <p className="text-[11px] text-[#5C5C5C] mt-1">Tasks below are preset from this job site.</p>
                  )}
                </div>
                <div>
                  <Label>Attendance</Label>
                  <Select value={form.attendance || ''} onValueChange={(v) => setForm((p) => ({ ...p, attendance: v === '__none__' ? '' : v }))}>
                    <SelectTrigger data-testid="jr-form-attendance"><SelectValue placeholder="Select attendance…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {(meta?.attendance_options || ['Present at Job Site', 'Present No Job Site', 'Tardy', 'Absent Excused', 'Absent Unexcused']).map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-[#1A1A1A]">Job Task Performance</h3>
                {!form.job_site_id && form.tasks.length === 0 && (
                  <p className="text-sm text-[#5C5C5C] border border-dashed border-slate-200 rounded-lg p-3" data-testid="jr-tasks-empty">
                    Choose a job site above to load its tasks. You can also add extra tasks below.
                  </p>
                )}
                {form.tasks.map((t, ti) => (
                  <div key={ti} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`jr-task-${ti}`}>
                    <div className="flex gap-2 items-center">
                      {t.complexity && (
                        <span className={`shrink-0 w-7 h-7 rounded-md text-xs font-bold flex items-center justify-center ${CX_STYLE[t.complexity]?.solid || ''}`}
                          title={`Complexity ${t.complexity}`} data-testid={`jr-task-cx-${ti}`}>
                          L{t.complexity}
                        </span>
                      )}
                      {t.locked ? (
                        <span className="flex-1 text-sm font-medium text-[#1A1A1A] px-1" data-testid={`jr-task-name-${ti}`}>{t.name}</span>
                      ) : (
                        <>
                          <Input className="flex-1" placeholder={`Added task — e.g. Stocking dairy shelves`} value={t.name}
                            onChange={(e) => setForm((p) => ({ ...p, tasks: p.tasks.map((x, i) => (i === ti ? { ...x, name: e.target.value } : x)) }))}
                            data-testid={`jr-task-name-${ti}`} />
                          <div className="flex gap-1 shrink-0">
                            {[1, 2, 3].map((cNum) => (
                              <button key={cNum} type="button" title={`Complexity ${cNum}`}
                                onClick={() => setForm((p) => ({ ...p, tasks: p.tasks.map((x, i) => (i === ti ? { ...x, complexity: cNum } : x)) }))}
                                className={`w-7 h-7 rounded text-xs font-bold border transition-colors ${t.complexity === cNum ? CX_STYLE[cNum].solid : 'bg-white border-slate-300 text-slate-500 hover:border-slate-400'}`}
                                data-testid={`jr-task-setcx-${ti}-${cNum}`}>{cNum}</button>
                            ))}
                          </div>
                          <Button size="sm" variant="ghost" className="text-red-600 shrink-0"
                            onClick={() => setForm((p) => ({ ...p, tasks: p.tasks.filter((_, i) => i !== ti) }))}
                            aria-label={`Remove task ${ti + 1}`} data-testid={`jr-task-remove-${ti}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                      {meta.task_dims.map((d) => (
                        <div key={d.key} className="flex items-center justify-between gap-2">
                          <span className="text-sm text-[#1A1A1A]">{d.label}</span>
                          <ScorePicker value={t[d.key]} label={d.label} levels={levels} testid={`jr-t${ti}-${d.key}`}
                            onChange={(v) => setForm((p) => ({ ...p, tasks: p.tasks.map((x, i) => (i === ti ? { ...x, [d.key]: v } : x)) }))} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {form.tasks.length < 12 && (
                  <Button variant="outline" size="sm" className="border-dashed"
                    onClick={() => setForm((p) => ({ ...p, tasks: [...p.tasks, { ...emptyTask(), complexity: 1 }] }))} data-testid="jr-add-task">
                    <Plus className="w-4 h-4 mr-1" /> Add another task
                  </Button>
                )}
              </div>

              <div className="space-y-1.5">
                <h3 className="font-semibold text-[#1A1A1A]">Soft Skills</h3>
                {meta.soft_skills.map((s) => (
                  <div key={s.key} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-[#1A1A1A]">{s.label}</span>
                    <ScorePicker value={form.soft_skills[s.key]} label={s.label} levels={levels} testid={`jr-ss-${s.key}`}
                      onChange={(v) => setForm((p) => ({ ...p, soft_skills: { ...p.soft_skills, [s.key]: v } }))} />
                  </div>
                ))}
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea rows={3} placeholder="Anything worth remembering about today…" value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="jr-form-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button className="bg-[#3d7a22] hover:bg-[#5aab3d]" onClick={save} disabled={saving} data-testid="jr-form-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} {editId ? 'Save changes' : 'Save report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* email dialog */}
      <Dialog open={!!emailFor} onOpenChange={(o) => !o && setEmailFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Email this report</DialogTitle>
            <DialogDescription>{emailFor && `${emailFor.intern_name} · ${emailFor.report_date} — the PDF will be attached.`}</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="Email addresses, separated by commas or new lines…" value={emailList}
            onChange={(e) => setEmailList(e.target.value)} data-testid="jr-email-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailFor(null)}>Cancel</Button>
            <Button className="bg-[#34657f] hover:bg-[#2a5268] text-white" onClick={sendEmail} data-testid="jr-email-send">
              <Mail className="w-4 h-4 mr-1.5" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobsiteReports;
